/**
 * Service layer — AR Billing (Readiness + Billing Run).
 *
 * SINGLE data seam for the two billing tools ported from Softr
 * (examples/billing_readiness.html, examples/billing_run.html). Owns every table id and query;
 * the page components consume normalized { id, fields } Airtable records and never touch
 * /api/airtable or BQE directly. When these tables migrate to Supabase, only this file changes.
 *
 * BQE Core + Slack calls go to Clerk-gated server endpoints (api/bqe-*, api/billing-remind) which
 * mint the BQE access token and read the Slack bot token SERVER-SIDE — no secret ships to the
 * browser (unlike the original Softr pages).
 */

import { apiGet, apiGetAll, apiGetOne, apiPost, apiPatch, apiDelete } from './_api.js'
import { getReviewRecords } from './timecards.js'
import { listPolicies, listHolidays } from './timeGate.js'

// ── Airtable tables ─────────────────────────────────────────────────────────────
const TBL_RUNS     = 'tblBq6BI2ptG0xBS8'   // Billing Runs
const TBL_LI       = 'tblc9mP1HrHGy0z2Y'   // Billing Line Items
const TBL_MEMBERS  = 'tblzax2FPEul41fjN'
const TBL_EXPENSES = 'tbl6qOAJn9C80PVkf'   // Reimbursable Expenses
const TBL_STAGING  = 'tblksH7rbKDI4gWeZ'   // Time entry staging
const TBL_PHASES   = 'tblNl3JpfIEWk9tPX'
const TBL_PROJECTS = 'tblgWX330MPJkhQVq'

const BILLING_MEMBER_TYPES = ['Team - Principal', 'Team - Manager', 'Team - Approved', 'Team - Bookkeeper']

// ── Clerk-authed server calls (no browser secret) ───────────────────────────────
async function authToken() { return window.__clerkGetToken ? await window.__clerkGetToken() : null }
async function authedPost(path, body) {
  const token = await authToken()
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body || {}),
  })
  let data = {}; try { data = await res.json() } catch (e) { data = {} }
  if (!res.ok && data && !data.error) data.error = 'Request failed: ' + res.status
  return data
}

// ── Billing Runs ─────────────────────────────────────────────────────────────────
/** The run for a given month/year, or null. */
export async function getRunForMonth(month, year) {
  const qs = 'filterByFormula=' + encodeURIComponent(`AND({Month}='${month}',{Year}='${year}')`) + '&maxRecords=1'
  const recs = await apiGet(TBL_RUNS, qs)
  return recs[0] || null
}

/** A run by record id, or null. */
export function getRunById(id) {
  return apiGetOne(TBL_RUNS, id)
}

/** Create a run record. Returns the created { id, fields }. */
export function createRun(fields) {
  return apiPost(TBL_RUNS, fields)
}

/** Patch a run record. Returns the raw Airtable response. */
export function updateRun(id, fields) {
  return apiPatch(TBL_RUNS, id, fields)
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/**
 * The most recent Billing Run strictly before the given (month, year) — used to find where the
 * LAST run's T+E window actually left off, so a skipped or late month doesn't quietly narrow the
 * next run's coverage. Ordered by (Year, Month) computed in JS from the plain text fields, not
 * Airtable's "Month Sort" formula field (formula fields don't survive the eventual Supabase
 * migration — see CLAUDE.md's derivation-register guidance).
 */
export async function getMostRecentRun(beforeMonth, beforeYear) {
  const all = await apiGetAll(TBL_RUNS, '')
  if (!all || !all.length) return null
  const beforeKey = Number(beforeYear) * 12 + MONTH_NAMES.indexOf(beforeMonth)
  let best = null, bestKey = -1
  for (const r of all) {
    const mi = MONTH_NAMES.indexOf(r.fields['Month'])
    if (mi < 0) continue
    const key = (Number(r.fields['Year']) || 0) * 12 + mi
    if (key < beforeKey && key > bestKey) { bestKey = key; best = r }
  }
  return best
}

/** Last calendar day of `month1indexed` (1=January) in `year`, walked back to the last Friday. */
function lastFridayOf(year, month1indexed) {
  const d = new Date(year, month1indexed, 0)
  while (d.getDay() !== 5) d.setDate(d.getDate() - 1)
  return d
}

/** A run's own T+E window END, derived purely from its Month/Year fields — the same formula used
 *  when that run was originally created (one month back from the run's own month). Lets us find
 *  where a past run's coverage actually stopped without needing to have stored it anywhere. */
function runWindowEnd(monthName, year) {
  const runMonth1 = MONTH_NAMES.indexOf(monthName) + 1
  if (runMonth1 < 1) return null
  const prevMonth = runMonth1 > 1 ? runMonth1 - 1 : 12
  const prevYear = runMonth1 > 1 ? Number(year) : Number(year) - 1
  return lastFridayOf(prevYear, prevMonth)
}

/**
 * The T+E review window for the run being prepared/worked right now. END is always the last
 * Friday of last month (the same "how far can we trust entries to be finalized" cutoff as
 * before). START is the day after the MOST RECENT PRIOR run's own window ended — so skipping or
 * running late on a month automatically widens the next window to cover everything since work
 * was last actually reviewed, instead of silently always looking back exactly one month
 * regardless of history. Falls back to the original fixed one-month lookback when there's no
 * prior run to anchor against (the very first run ever, or if the lookup fails).
 */
export async function getBillingPeriod() {
  const now = new Date()
  const runMonth1 = now.getMonth() + 1, runYear = now.getFullYear()
  const prevMonth = runMonth1 > 1 ? runMonth1 - 1 : 12
  const prevYear = runMonth1 > 1 ? runYear : runYear - 1
  const periodEnd = lastFridayOf(prevYear, prevMonth)

  let periodStart = null
  try {
    const last = await getMostRecentRun(MONTH_NAMES[runMonth1 - 1], runYear)
    if (last) {
      const lastEnd = runWindowEnd(last.fields['Month'], last.fields['Year'])
      if (lastEnd) { periodStart = new Date(lastEnd); periodStart.setDate(periodStart.getDate() + 1) }
    }
  } catch (e) { /* fall through to the default below */ }

  if (!periodStart) {
    const twoAgo = prevMonth > 1 ? prevMonth - 1 : 12
    const twoAgoYear = prevMonth > 1 ? prevYear : prevYear - 1
    const priorClose = lastFridayOf(twoAgoYear, twoAgo)
    periodStart = new Date(priorClose)
    periodStart.setDate(periodStart.getDate() + 3)
  }
  return { start: periodStart, end: periodEnd }
}

// ── Billing Line Items ─────────────────────────────────────────────────────────────
/**
 * All line items for a run, matched by the run's record id via the reliable
 * {Record ID (from Billing Runs)} lookup (ARRAYJOIN on the raw link field returns primary
 * values, not ids — see CLAUDE.md Airtable rule #6). Optionally sorted by Name.
 */
export function getLineItemsForRun(runId, { sortByName = false } = {}) {
  let qs = 'filterByFormula=' + encodeURIComponent(`FIND('${runId}',ARRAYJOIN({Record ID (from Billing Runs)},','))`)
  if (sortByName) qs += '&sort%5B0%5D%5Bfield%5D=Name&sort%5B0%5D%5Bdirection%5D=asc'
  // strict: throw on a load failure so the Run shows a retry, not a false "0 projects".
  return apiGetAll(TBL_LI, qs, undefined, { strict: true })
}

export function createLineItem(fields) { return apiPost(TBL_LI, fields) }
export function updateLineItem(id, fields) { return apiPatch(TBL_LI, id, fields) }
export function deleteLineItem(id) { return apiDelete(TBL_LI, id) }

// ── Projects ─────────────────────────────────────────────────────────────────────
/**
 * Projects that should get a Billing Line Item in a new run: every Executed project (business as
 * usual) PLUS any non-Executed project (On Hold, Completed, Cancelled, or anything else) that
 * still has OPEN AR in Core — a posted, non-void/draft invoice with balance > 0.
 *
 * Why: Contract Stage leaving 'Executed' (Project Hold, Close Out Project) is exactly what makes
 * the run stop creating line items for a project. Without this, a project's last unpaid invoice
 * would go dark the moment someone puts it on hold, completes it, or cancels it — nobody gets a
 * line item, nobody gets prompted to chase it. This keeps it surfacing every month, with its own
 * payment-follow-up checklist, until the AR actually clears (see docs/BILLING_BQE.md's
 * "Post-posting payment follow-up" section for what that checklist does).
 *
 * The open-AR match reuses the exact same filter as `getProjectInvoiceStatus` (posted, non-void,
 * non-draft, balance > 0.005, matched by `Core Project UID`) so a project both surfaces here AND
 * shows the correct aging once its line item is opened.
 */
// The firm's own projects (number 00 or 00-xx: Overhead, Standard Sheets) are never billed and never
// get a line item (Matt, 2026-11-03), even though they carry Contract Stage = Executed.
export function isFirmInternalProject(p) {
  return /^00(\b|-|$)/.test(String((p && p.fields && p.fields['Project Number']) || '').trim())
}

export async function getBillingRunProjects() {
  const executedQs = 'filterByFormula=' + encodeURIComponent(`FIND('Executed',ARRAYJOIN({Contract Stage},','))`)
  const executed = (await apiGetAll(TBL_PROJECTS, executedQs)).filter(p => !isFirmInternalProject(p))

  const nonExecutedQs = 'filterByFormula=' + encodeURIComponent(
    `AND(NOT(FIND('Executed',ARRAYJOIN({Contract Stage},','))),{Core Project UID}!='')`
  )
  const nonExecuted = await apiGetAll(TBL_PROJECTS, nonExecutedQs)
  if (!nonExecuted.length) return executed

  const invoices = await getCoreInvoicesCached()
  if (!Array.isArray(invoices) || !invoices.length) return executed

  const openArProjects = nonExecuted.filter(p => {
    const uid = cleanCoreUid(p.fields['Core Project UID'])
    if (!uid) return false
    return invoices.some(inv => {
      if (inv.isVoid || inv.isDraft || inv.status !== 1) return false
      if ((Number(inv.balance) || 0) <= 0.005) return false
      return (inv.invoiceDetails || []).some(d => d.rootProjectId === uid)
    })
  })
  return executed.concat(openArProjects.filter(p => !isFirmInternalProject(p)))
}

/**
 * Batch-resolve { projectId: coreProjectUid } for a set of Project record ids (chunked
 * OR(RECORD_ID()=…) filter — the Run's own approved-total summary uses this to match line items
 * back to their Core invoices without a per-item Phases round trip).
 */
export async function getProjectCoreUids(projectIds) {
  const list = Array.isArray(projectIds) ? [...new Set(projectIds.filter(Boolean))] : []
  const out = {}
  for (let i = 0; i < list.length; i += 50) {
    const chunk = list.slice(i, i + 50)
    const formula = 'OR(' + chunk.map(id => `RECORD_ID()='${id}'`).join(',') + ')'
    const recs = await apiGet(TBL_PROJECTS, 'filterByFormula=' + encodeURIComponent(formula))
    recs.forEach(r => { out[r.id] = cleanCoreUid(r.fields['Core Project UID']) })
  }
  return out
}

// ── Members ────────────────────────────────────────────────────────────────────────
/** Billing-relevant team members (principals/managers/approved/bookkeepers). */
export async function getBillingMembers() {
  const all = await apiGetAll(TBL_MEMBERS, '')
  return all.filter(m => BILLING_MEMBER_TYPES.indexOf(m.fields['Member Type'] || '') !== -1)
}

// ── Reimbursable expenses ────────────────────────────────────────────────────────
/** All reimbursable expense records (caller filters by Workflow Status). */
export function getReimbursables() {
  return apiGetAll(TBL_EXPENSES, '')
}

/** The one status value that means an expense has reached BQE Core. There is NO automated push for
 *  expenses (unlike time) — a human sets this by hand, so it's the only signal available. Named as a
 *  constant because the string is easy to get wrong: `Logged into Core` is the name of a FORMULA
 *  FIELD on that table, not a Workflow Status choice, and code that compared against it silently
 *  matched nothing (see the 2026-08 fix in docs/TIME_RECORDS.md). */
export const EXPENSE_IN_CORE_STATUS = 'Recorded in BQE Core'

/**
 * The real dollar amount of an expense. Airtable's own `Total Cost` formula is `Units * Unit Cost`,
 * which silently yields $0 for every expense that has a Unit Cost but no Units — permit fees,
 * reprographics, shipping, meals, i.e. most of them. Confirmed against live data 2026-08: all 100
 * sampled records read $0.00, hiding $6,846 of Unit Cost in that sample alone. Mileage is the
 * inverse case (Units set, no Unit Cost) and has no dollar value of its own until a rate is applied.
 * Resolve it here so the app is right regardless of what the Airtable formula says.
 */
export function expenseAmount(rec) {
  const f = (rec && rec.fields) || {}
  const total = Number(f['Total Cost']) || 0
  if (total > 0) return total
  const units = Number(f['Units']) || 0
  const unit = Number(f['Unit Cost']) || 0
  return units > 0 ? units * unit : unit
}

// ── Time (Supabase — the live source after the time-entry cutover) ─────────────────
/**
 * All time entries with Entry Date in [startISO, endISO] across the firm, read from Supabase
 * (public.time_entries) via the time-cards service. Returns the same { id, fields } shape the
 * readiness tool already consumes ('Submitter', 'Actual Hours', 'Status', 'Entry Date', 'Member Name').
 */
export function getTimeEntriesInPeriod(startISO, endISO) {
  return getReviewRecords(startISO, endISO)
}

/**
 * Time entries for ONE project in a period, from Supabase (replaces the legacy Airtable staging
 * read the Billing Run's "new billing" signal used). The period's firm-wide entries are fetched
 * once per period and cached, then filtered to the project by its Airtable record UID.
 * Returns the same { id, fields } shape (fields: 'Status', 'Actual Hours', 'Phase Airtable ID', …).
 */
const _periodEntriesCache = {}
export function getProjectTimeEntriesInPeriod(projectRecordUid, startISO, endISO) {
  const key = startISO + '|' + endISO
  if (!_periodEntriesCache[key]) _periodEntriesCache[key] = getReviewRecords(startISO, endISO).catch(() => [])
  return _periodEntriesCache[key].then(all => (all || []).filter(e => {
    const uids = e.fields['Project Record UID'] || []
    return uids.indexOf(projectRecordUid) !== -1
  }))
}

/**
 * The firm's time policy for computing "expected hours": per-member employment/schedule (so
 * contractors aren't gated by hours) + firm settings (full_time_daily_min, weekly_target).
 * Gated to admins/time reviewers; degrades to an empty policy (caller falls back to a default).
 * Returns { members: [{ id, policy:{employment_type,schedule,enforced}|null }], settings, holidays:[ISO] }.
 */
export async function getTimePolicy() {
  try {
    const [{ members, settings }, holidays] = await Promise.all([
      listPolicies(),
      listHolidays().catch(() => []),
    ])
    return { members: members || [], settings: settings || null, holidays: (holidays || []).map(h => h.holiday_date) }
  } catch (e) {
    return { members: [], settings: null, holidays: [] }
  }
}

// ── Time staging (legacy Airtable — still used by the Run tool's per-project signals) ──
/** Staging entries with Entry Date in (startISO, endISO] across all projects. */
export function getStagingEntriesInPeriod(startISO, endISO) {
  const dayBefore = isoMinusOneDay(startISO)
  const qs = 'filterByFormula=' + encodeURIComponent(
    `AND(IS_AFTER({Entry Date},'${dayBefore}'),NOT(IS_AFTER({Entry Date},'${endISO}')))`)
  return apiGetAll(TBL_STAGING, qs)
}

/** Approved/Submitted/Posted staging entries for one project in (startISO, endISO]. */
export function getStagingEntriesForProjectInPeriod(projectRecordUid, startISO, endISO) {
  const dayBefore = isoMinusOneDay(startISO)
  const qs = 'filterByFormula=' + encodeURIComponent(
    `AND(FIND('${projectRecordUid}',ARRAYJOIN({Project Record UID},',')),` +
    `IS_AFTER({Entry Date},'${dayBefore}'),NOT(IS_AFTER({Entry Date},'${endISO}')),` +
    `OR({Status}='Approved',{Status}='Submitted',{Status}='Posted'))`)
  return apiGetAll(TBL_STAGING, qs)
}

function isoMinusOneDay(iso) {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() - 1)
  return d.toISOString().split('T')[0]
}

// ── Phases ───────────────────────────────────────────────────────────────────────
/** Phases for a project (matched by record-id UID), sorted by Sort Order. */
export function getPhasesForProject(projectRecordUid) {
  const qs = 'filterByFormula=' + encodeURIComponent(`FIND('${projectRecordUid}',ARRAYJOIN({Project Record UID (from Project)},','))`)
    + '&sort%5B0%5D%5Bfield%5D=Sort+Order&sort%5B0%5D%5Bdirection%5D=asc'
  return apiGetAll(TBL_PHASES, qs)
}

/**
 * Phases for many projects at once → { projectRecordUid: [phase records sorted by Sort Order] }.
 * The Billing Run's pre-flight answers every project when the run opens; one query per 20 projects
 * instead of one per project keeps that inside Airtable's rate limit.
 */
export async function getPhasesForProjects(projectRecordUids) {
  const list = Array.isArray(projectRecordUids) ? [...new Set(projectRecordUids.filter(Boolean))] : []
  const out = {}
  list.forEach(id => { out[id] = [] })
  for (let i = 0; i < list.length; i += 20) {
    const chunk = list.slice(i, i + 20)
    const formula = 'OR(' + chunk.map(id => `FIND('${id}',ARRAYJOIN({Project Record UID (from Project)},','))`).join(',') + ')'
    const recs = await apiGetAll(TBL_PHASES, 'filterByFormula=' + encodeURIComponent(formula))
    recs.forEach(r => {
      // The Project link carries record ids reliably over the API; the UID lookup is a fallback.
      const uids = [].concat(r.fields['Project'] || [], r.fields['Project Record UID (from Project)'] || [])
      chunk.forEach(id => { if (uids.indexOf(id) !== -1) out[id].push(r) })
    })
  }
  Object.keys(out).forEach(id => out[id].sort((a, b) => (a.fields['Sort Order'] || 0) - (b.fields['Sort Order'] || 0)))
  return out
}

export function updatePhase(id, fields) { return apiPatch(TBL_PHASES, id, fields) }

// ── BQE Core (server-minted token; Clerk-gated endpoints) ──────────────────────────
/** Retrieve a Core phase (project) — returns { percentComplete, ... } or { error }. */
export function getCorePhase(phaseId) {
  return authedPost('/api/bqe-phase', { phaseId })
}

/** List a root project's Core invoices (for billed-to-date per phase). Returns an array. */
export async function getCoreInvoices(rootProjectId) {
  const data = await authedPost('/api/bqe-invoices', { rootProjectId })
  if (data && Array.isArray(data.invoices)) {
    if (data._debug) { try { console.log('[billing] bqe-invoices fetch:', data._debug) } catch (e) {} }
    return data.invoices
  }
  return Array.isArray(data) ? data : []
}

// A Core Project UID sometimes gets pasted with the URL tail (e.g. "GUID?rootId=GUID"). Core's
// invoices carry just the bare GUID, so strip anything from "?" onward (and whitespace) before matching.
function cleanCoreUid(uid) { return String(uid || '').split('?')[0].trim() }

// ── Firm-wide invoice cache ──────────────────────────────────────────────────────
// BQE caps the invoice list at 100/page, so a project's billed-to-date means scanning the firm's
// whole invoice history. Do that ONCE per session (prefetch on Billing-Run open), cache the slim
// result client-side, and let every phase compute its billed from memory — no re-scan per modal.
let _invCache = null      // slim invoices once loaded
let _invPromise = null     // in-flight prefetch (dedupes concurrent callers)

/** Kick off (or reuse) the one-time firm invoice load. Returns a promise of the slim invoice array.
 *  `force` (the manual "re-pull" refresh button) also bypasses the server's own shared cache —
 *  see api/_bqeCache.js — not just this client-side one. */
export function prefetchCoreInvoices(force) {
  if (!force && _invCache) return Promise.resolve(_invCache)
  if (!force && _invPromise) return _invPromise
  _invPromise = authedPost('/api/bqe-invoices', { all: true, force: !!force }).then(function (data) {
    const invs = (data && Array.isArray(data.invoices)) ? data.invoices : (Array.isArray(data) ? data : [])
    if (data && data._debug) { try { console.log('[billing] prefetch invoices:', data._debug) } catch (e) {} }
    _invCache = invs
    return invs
  }).catch(function () { _invPromise = null; return [] })
  return _invPromise
}
/** The cached firm invoices, awaiting the prefetch if it's still loading (or starting it). */
export function getCoreInvoicesCached() {
  if (_invCache) return Promise.resolve(_invCache)
  return prefetchCoreInvoices()
}
/** True once the firm invoices are cached. */
export function coreInvoicesReady() { return _invCache != null }

/**
 * Open-invoice / aging status for one project, computed from the cached Core invoices (no extra
 * fetch). "Open" = a non-void/draft invoice with balance > 0 whose details belong to this project
 * (matched by root Core UID). Aging = days the OLDEST open invoice has been outstanding (from its
 * date), bucketed to the run's cycle thresholds: ≤30d one cycle (grace), 31–60d two cycles
 * (interest), >60d three+ (additional interest). Returns a SUGGESTION; the bookkeeper still confirms.
 */
export async function getProjectInvoiceStatus(rootCoreUid, asOfISO) {
  const empty = { openCount: 0, openTotal: 0, oldestDate: null, oldestNumber: '', daysOutstanding: 0, suggestion: null }
  rootCoreUid = cleanCoreUid(rootCoreUid)
  if (!rootCoreUid) return empty
  const invoices = await getCoreInvoicesCached()
  if (!Array.isArray(invoices)) return empty
  const asOf = asOfISO ? new Date(asOfISO + 'T00:00:00') : new Date()
  let openCount = 0, openTotal = 0, oldest = null, oldestNumber = '', matched = 0, sawBalance = false
  for (const inv of invoices) {
    if (inv.isVoid || inv.isDraft) continue
    if (inv.status !== 1) continue   // posted only — exclude un-posted/retainer (matches Core's AR)
    const belongs = (inv.invoiceDetails || []).some(d => d.rootProjectId === rootCoreUid)
    if (!belongs) continue
    matched++
    if (inv.balance !== undefined && inv.balance !== null) sawBalance = true
    const bal = Number(inv.balance) || 0
    if (bal <= 0.005) continue
    openCount++; openTotal += bal
    const d = inv.date ? new Date(inv.date) : null
    if (d && !isNaN(d) && (!oldest || d < oldest)) { oldest = d; oldestNumber = inv.invoiceNumber || '' }
  }
  // If this project has invoices but none carry a balance field, we can't judge "open" — say so
  // instead of falsely reporting zero open.
  if (matched > 0 && !sawBalance) return Object.assign({}, empty, { unknown: true })
  if (!oldest) return Object.assign({}, empty, { openCount, openTotal })
  const days = Math.max(0, Math.round((asOf - oldest) / 86400000))
  const suggestion = days <= 30 ? '30 days' : days <= 60 ? '60 days' : '90+ days'
  return { openCount, openTotal, oldestDate: oldest.toISOString().split('T')[0], oldestNumber, daysOutstanding: days, suggestion }
}

// Days after posting before a still-unpaid invoice needs a required follow-up reminder.
export const PAYMENT_FOLLOWUP_DAYS = 14

/**
 * Last-resort invoice resolution for sending a payment-followup reminder when the line item has NO
 * Invoice Number on file at all — exactly the case that leads here: Core couldn't be checked
 * automatically (the `unknown` state), a human manually flagged it still unpaid, and now the
 * reminder needs SOME real Core invoice id to attach to (the send endpoint requires one — see
 * api/billing-send-invoice.js). Independently resolves the project's own Core UID, then finds its
 * oldest open (non-void/draft, posted, balance > 0) invoice — same matching rules as
 * getProjectInvoiceStatus, just also keeping the invoice `id` that function doesn't expose. Returns
 * null if the project has no Core UID on file or no open invoice matches (genuinely nothing to
 * attach — the caller should surface that rather than send a reminder pointing at nothing).
 */
export async function getFallbackInvoiceForProject(projectAirtableId) {
  if (!projectAirtableId) return null
  const proj = await apiGetOne(TBL_PROJECTS, projectAirtableId)
  const rootCoreUid = cleanCoreUid(proj && proj.fields && proj.fields['Core Project UID'])
  if (!rootCoreUid) return null
  const invoices = await getCoreInvoicesCached()
  if (!Array.isArray(invoices)) return null
  let best = null
  for (const inv of invoices) {
    if (inv.isVoid || inv.isDraft) continue
    if (inv.status !== 1) continue
    const belongs = (inv.invoiceDetails || []).some(d => d.rootProjectId === rootCoreUid)
    if (!belongs) continue
    const bal = Number(inv.balance) || 0
    if (bal <= 0.005) continue
    const d = inv.date ? new Date(inv.date) : null
    if (!d || isNaN(d)) continue
    if (!best || d < best.date) best = { id: inv.id, invoiceNumber: inv.invoiceNumber || '', date: d, balance: bal }
  }
  return best ? { id: best.id, invoiceNumber: best.invoiceNumber, balance: best.balance } : null
}

/**
 * Precise invoice lookup for the Billing Run's post-posting follow-up steps — a sibling to
 * getProjectInvoiceStatus (which is project-wide/aggregate) that matches specific invoice(s) by
 * number against the cached firm invoice list. No rootCoreUid needed: invoice numbers are already
 * project-prefixed ("25-11" → "2511.05") and unique firm-wide, so this stays a pure in-memory
 * lookup — no per-project Core UID resolution, no extra network calls.
 *
 * `invoiceNumberOrCsv` may be a single number or a comma-separated list (a statement cycle tracks
 * every invoice that was open and shown to the client, not just the newest one — see
 * docs/BILLING_BQE.md). Any tracked number NOT found in the cache makes the WHOLE result
 * `{ found: false }` — never silently drop a missing/renumbered/voided invoice and call the rest
 * "paid" while money could still be owed on it.
 */
export async function getInvoicePaymentStatus(invoiceNumberOrCsv) {
  if (!invoiceNumberOrCsv) return { found: false }
  const numbers = String(invoiceNumberOrCsv).split(',').map(s => s.trim()).filter(Boolean)
  if (!numbers.length) return { found: false }
  const invoices = await getCoreInvoicesCached()
  const matches = []
  for (const num of numbers) {
    const inv = (invoices || []).find(x => x.invoiceNumber === num)
    if (!inv) return { found: false }
    matches.push(inv)
  }
  // First tracked number is "newest" by construction (the write path sorts newest-first before
  // joining) — matches[0] is what a reminder email should name and whose id a reply should attach to.
  const first = matches[0]
  let unknown = false
  let openTotal = 0
  let minDaysSinceIssue = null
  let allPaid = true
  for (const inv of matches) {
    if (inv.balance === undefined || inv.balance === null) { unknown = true; continue }
    const bal = Number(inv.balance) || 0
    if (bal > 0.005) { allPaid = false; openTotal += bal }
    const d = inv.date ? new Date(inv.date) : null
    if (d && !isNaN(d)) {
      const days = Math.max(0, Math.round((Date.now() - d.getTime()) / 86400000))
      if (minDaysSinceIssue === null || days < minDaysSinceIssue) minDaysSinceIssue = days
    }
  }
  if (unknown) return { found: true, unknown: true, id: first.id, balance: openTotal, openCount: matches.length }
  return {
    found: true, paid: allPaid, balance: openTotal, daysSinceIssue: minDaysSinceIssue,
    id: first.id, isVoid: !!first.isVoid, openCount: matches.length,
  }
}

// ── Firm-wide phase percent-complete cache ─────────────────────────────────────────
// Same idea as invoices: load every Core phase's % ONCE on Billing-Run open (one server call,
// one token mint — no per-modal race) and let each phase modal read from this map by Core UID.
let _phaseCache = null      // { [coreId]: percentComplete (0–100) }
let _phasePromise = null

/** Kick off (or reuse) the one-time firm phase-% load. Returns a promise of the { coreId: pct } map.
 *  `force` (the manual "re-pull" refresh button) also bypasses the server's own shared cache. */
export function prefetchCorePhases(force) {
  if (!force && _phaseCache) return Promise.resolve(_phaseCache)
  if (!force && _phasePromise) return _phasePromise
  _phasePromise = authedPost('/api/bqe-phases', { force: !!force }).then(function (data) {
    const arr = (data && Array.isArray(data.phases)) ? data.phases : []
    if (data && data._debug) { try { console.log('[billing] prefetch phases:', data._debug) } catch (e) {} }
    // Only cache phases whose % actually came back, so any phase the list endpoint omits falls back
    // to a direct read (rather than rendering a misleading "n/a").
    const map = {}
    for (let i = 0; i < arr.length; i++) {
      const p = arr[i]
      if (p && p.id != null && p.percentComplete !== undefined && p.percentComplete !== null) map[p.id] = p.percentComplete
    }
    _phaseCache = map
    return map
  }).catch(function () { _phasePromise = null; return {} })
  return _phasePromise
}
/** The cached firm phase-% map, awaiting the prefetch if it's still loading (or starting it). */
export function getCorePhasesCached() {
  if (_phaseCache) return Promise.resolve(_phaseCache)
  return prefetchCorePhases()
}
/** True once the firm phase %s are cached. */
export function corePhasesReady() { return _phaseCache != null }

/** Drop the firm caches so the next prefetch re-pulls from Core (used by the manual Refresh button). */
export function resetCoreCaches() {
  _invCache = null; _invPromise = null
  _phaseCache = null; _phasePromise = null
  _unbilledExpPromise = null
}

// ── Expenses waiting to be billed ────────────────────────────────────────────────
let _unbilledExpPromise = null
/**
 * Billable Core expense entries not on any invoice yet, firm-wide (one read per session, reset with
 * the other Core caches). Each is on a PHASE's Core id. Resolves to an array, or rejects when Core
 * couldn't be read in full: the pre-flight must not read a failed fetch as "nothing to bill".
 */
export function getUnbilledCoreExpenses() {
  if (!_unbilledExpPromise) {
    _unbilledExpPromise = authedPost('/api/bqe-invoices', { unbilledExpenses: true }).then(function (data) {
      if (!data || data.error || !Array.isArray(data.entries)) throw new Error((data && data.error) || 'Core expense entries unavailable')
      return data.entries
    })
    _unbilledExpPromise.catch(function () { _unbilledExpPromise = null })
  }
  return _unbilledExpPromise
}

/**
 * Expense reports filed in Cortex that haven't reached Core yet, grouped by Airtable project id:
 * { projectId: [{ status, payor, amount, date }] }. Submitted = waiting for approval; Approved =
 * approved but not yet in Core (a company-card charge waits for Bookkeeping to post it).
 */
export async function getPendingExpenseReports() {
  const formula = "OR({Workflow Status}='Submitted',{Workflow Status}='Approved')"
  const recs = await apiGetAll(TBL_EXPENSES, 'filterByFormula=' + encodeURIComponent(formula))
  const out = {}
  recs.forEach(function (r) {
    const pid = (r.fields['Project'] || [])[0]
    if (!pid) return   // overhead is never billed
    ;(out[pid] = out[pid] || []).push({
      status: r.fields['Workflow Status'] || '',
      payor: r.fields['Payor'] || 'Employee',
      amount: expenseAmount(r),
      date: r.fields['Expense Date'] || null,
    })
  })
  return out
}

/**
 * Firm-wide AR snapshot from the cached Core invoices (no extra fetch): total outstanding =
 * Σ balance of non-void/draft invoices with balance > 0, bucketed by age of the due date (falling
 * back to invoice date): Current ≤30d, 30–60d, 60+d. `hasData` is false if invoices carry no
 * balance field (so the UI can say so rather than show $0).
 */
let _arLogged = false
export async function getArSnapshot(asOfISO) {
  const invoices = await getCoreInvoicesCached()
  const asOf = asOfISO ? new Date(asOfISO + 'T00:00:00') : new Date()
  let outstanding = 0, current = 0, d30_60 = 0, d60plus = 0, hasData = false
  // Diagnostics to reconcile against Core's AR total.
  const dbg = { total: (invoices || []).length, skippedVoid: 0, skippedDraft: 0, noBalanceField: 0, zeroBal: 0, counted: 0, interestCount: 0, interestSum: 0, byStatus: {}, top: [] }
  for (const inv of (invoices || [])) {
    if (inv.isVoid) { dbg.skippedVoid++; continue }
    if (inv.isDraft) { dbg.skippedDraft++; continue }
    // status 1 = posted (real AR); 0 = un-posted (retainer / not-yet-sent). Core's AR counts only
    // posted invoices — un-posted retainers don't show there. (Confirmed: status-1 ≈ Core's total.)
    if (inv.status !== 1) { dbg.skippedUnposted = (dbg.skippedUnposted || 0) + 1; continue }
    if (inv.balance === undefined || inv.balance === null) { dbg.noBalanceField++; continue }
    hasData = true
    const bal = Number(inv.balance) || 0
    if (bal <= 0.005) { dbg.zeroBal++; continue }
    outstanding += bal; dbg.counted++
    const isInterest = inv.isLateFeeInvoice === true || String(inv.invoiceNumber || '').toUpperCase().indexOf('-INT') !== -1
    if (isInterest) { dbg.interestCount++; dbg.interestSum += bal }
    const sk = String(inv.status)
    if (!dbg.byStatus[sk]) dbg.byStatus[sk] = { count: 0, sum: 0 }
    dbg.byStatus[sk].count++; dbg.byStatus[sk].sum += bal
    dbg.top.push({ num: inv.invoiceNumber, bal: Math.round(bal), status: inv.status, draft: inv.isDraft, lateFee: inv.isLateFeeInvoice })
    const ref = inv.dueDate || inv.date
    const dt = ref ? new Date(ref) : null
    const days = (dt && !isNaN(dt)) ? Math.floor((asOf - dt) / 86400000) : 0
    if (days > 60) d60plus += bal
    else if (days > 30) d30_60 += bal
    else current += bal
  }
  if (!_arLogged) {
    _arLogged = true
    try {
      Object.keys(dbg.byStatus).forEach(k => { dbg.byStatus[k].sum = Math.round(dbg.byStatus[k].sum) })
      dbg.top.sort((a, b) => b.bal - a.bal); dbg.top = dbg.top.slice(0, 8)
      dbg.interestSum = Math.round(dbg.interestSum)
      console.log('[billing] AR breakdown — outstanding=' + Math.round(outstanding), dbg)
    } catch (e) {}
  }
  return { outstanding, current, d30_60, d60plus, hasData }
}

/** Staff-facing billing-hold list — { holds: [...], asOf } — see api/billing-holds.js for shape. */
export function getBillingHolds() {
  return authedPost('/api/billing-holds', {})
}

/** Push percentComplete to a Core phase. Returns the updated phase or { error }. */
export function pushCorePhasePercent(phaseId, percentComplete) {
  return authedPost('/api/bqe-phase-update', { phaseId, percentComplete })
}

/** A project's invoices from the cached firm set (incl. DRAFTS — that's what you preview before
 *  sending; excludes only voids), newest first. For the PDF preview. */
export async function getProjectInvoices(rootCoreUid) {
  rootCoreUid = cleanCoreUid(rootCoreUid)
  if (!rootCoreUid) return []
  const invoices = await getCoreInvoicesCached()
  return (invoices || [])
    .filter(inv => inv.id && !inv.isVoid && (inv.invoiceDetails || []).some(d => d.rootProjectId === rootCoreUid))
    .map(inv => ({ id: inv.id, invoiceNumber: inv.invoiceNumber, date: inv.date, balance: inv.balance, posted: inv.status === 1, draft: !!inv.isDraft, lateFee: !!inv.isLateFeeInvoice }))
    .sort((a, b) => (b.draft - a.draft) || (new Date(b.date)) - (new Date(a.date)))   // drafts first, then newest
}

// Previously-billed per phase (Core UID) = Σ serviceAmount+fixedFee across POSTED, non-interest
// invoices, EXCLUDING the given invoice. Drives the invoice's "Previously Billed" / "Billed To Date".
async function previouslyBilledByPhase(excludeInvoiceId) {
  const invoices = await getCoreInvoicesCached()
  const map = {}
  for (const inv of (invoices || [])) {
    if (inv.id === excludeInvoiceId) continue
    if (inv.isVoid || inv.isDraft || inv.status !== 1) continue
    if (inv.isLateFeeInvoice === true || String(inv.invoiceNumber || '').toUpperCase().indexOf('-INT') !== -1) continue
    for (const d of (inv.invoiceDetails || [])) {
      if (!d.projectId) continue
      map[d.projectId] = (map[d.projectId] || 0) + (d.serviceAmount || 0) + (d.fixedFee || 0)
    }
  }
  return map
}

// ── Invoice summary footer (mirrors Core's custom report) ───────────────────────────
// Core does NOT expose its summary rollups on the invoice object (confirmed against a real
// interest invoice: no previousBalance / interest / paid fields). So we derive them from the
// cached firm invoices — the same source AR trusts — scoped to this invoice's root project:
//
//   currentFees        = the current invoice's invoiceAmount (the service invoice being previewed)
//   currentInterest    = Σ open balance of the account's OTHER late-fee invoices (interest charged)
//   previousBalanceDue = Σ open balance of all OTHER prior non-interest invoices
//   totalNowDue        = currentFees + currentInterest + previousBalanceDue (the bottom line)
//   billedToDate       = Σ invoiceAmount across the account (current + posted, non-void)
//   paidToDate         = billedToDate − totalNowDue
//
// The account = the current invoice (included even as a draft — it's what we preview) plus every
// POSTED (status 1), non-void invoice for the project DATED ON/BEFORE the current invoice (time-aware:
// the figures reflect the account as of this invoice's issue date, so a later interest charge never
// shows on an earlier invoice). Other drafts/un-posted are excluded.
// Interest- and previous-balance lines render only when > 0; the Account Summary box shows only
// when there's prior activity. Returns null if the invoice isn't in the cache (renderer falls back).
const _isInterestInv = inv => inv.isLateFeeInvoice === true || String(inv.invoiceNumber || '').toUpperCase().indexOf('-INT') !== -1
async function buildInvoiceSummary(invoiceId) {
  const invoices = await getCoreInvoicesCached()
  const cur = (invoices || []).find(i => i.id === invoiceId)
  if (!cur) return null
  const root = (cur.invoiceDetails || []).map(d => d.rootProjectId).find(Boolean)
  if (!root) return null
  const belongs = inv => (inv.invoiceDetails || []).some(d => d.rootProjectId === root)

  // If the previewed invoice IS itself an interest/late-fee invoice (no new services — just a finance
  // charge on overdue balances), its amount is current INTEREST, not current fees.
  const curIsInterest = _isInterestInv(cur)
  const curAmount = Number(cur.invoiceAmount) || 0
  const curBalance = Number(cur.balance) || 0
  const currentFees = curIsInterest ? 0 : curAmount
  // Time-aware: the summary reflects the account AS OF this invoice's issue date. An invoice (interest
  // or otherwise) that didn't exist when THIS one was issued must not appear on it — otherwise
  // re-previewing an older invoice shows a later interest charge. Combined with the open-balance gate
  // below, interest shows only on invoices issued after it was charged, and only until it's paid.
  const curDate = cur.date ? new Date(cur.date) : null
  let billedToDate = curAmount, previousBalanceDue = 0
  let currentInterest = curIsInterest ? curBalance : 0   // interest-only: this invoice's OPEN balance (0 once paid)
  const dbg = [{ num: cur.invoiceNumber, kind: curIsInterest ? 'current-interest' : 'current', status: cur.status, draft: !!cur.isDraft, amt: curAmount }]
  for (const inv of (invoices || [])) {
    if (inv.id === invoiceId) continue            // current handled above
    if (inv.isVoid) continue
    if (!belongs(inv)) continue
    if (curDate && inv.date) { const d = new Date(inv.date); if (!isNaN(d) && d > curDate) continue }   // skip invoices dated AFTER this one
    const interest = _isInterestInv(inv)
    const bal = Number(inv.balance) || 0
    // Posted (status 1) non-interest invoices are prior AR. Interest charged THIS cycle is often
    // still a DRAFT (just like the current invoice), so include any OPEN interest invoice regardless
    // of post state — otherwise the Current Interest line and billed-to-date silently drop it.
    const include = interest ? (inv.status === 1 || bal > 0.005) : (inv.status === 1)
    if (!include) continue
    billedToDate += Number(inv.invoiceAmount) || 0
    if (bal > 0.005) { if (interest) currentInterest += bal; else previousBalanceDue += bal }
    dbg.push({ num: inv.invoiceNumber, kind: interest ? 'interest' : 'prior', status: inv.status, draft: !!inv.isDraft, bal: Math.round(bal * 100) / 100, amt: inv.invoiceAmount })
  }
  try { console.log('[billing] invoice summary ' + (cur.invoiceNumber || invoiceId) + ' — fees=' + currentFees + ' interest=' + currentInterest + ' prev=' + Math.round(previousBalanceDue * 100) / 100 + ' billed=' + Math.round(billedToDate * 100) / 100, dbg) } catch (e) {}
  // "Now due" is based on BALANCES, not amounts — a PAID invoice shows $0 due (amount still shows as
  // Current Fees). currentDue = this (non-interest) invoice's still-owed (0 once paid).
  const currentDue = curIsInterest ? 0 : curBalance
  const totalNowDue = currentDue + currentInterest + previousBalanceDue
  const paidToDate = Math.max(0, billedToDate - totalNowDue)
  const paid = (curBalance <= 0.005) && (curAmount > 0.005)
  const hasFees = currentFees > 0.005
  const hasInterest = currentInterest > 0.005
  const hasPrevious = previousBalanceDue > 0.005
  return {
    currentFees, currentInterest, previousBalanceDue, totalNowDue, billedToDate, paidToDate, paid,
    totalCurrentBilling: currentFees + currentInterest,
    hasFees, hasInterest, hasPrevious,
    isInterestOnly: !hasFees && hasInterest,   // an interest/late-fee invoice with no new services
    // Account Summary box appears whenever the account has history beyond this single invoice.
    showAccountSummary: hasInterest || hasPrevious || paidToDate > 0.005,
  }
}

/** Render the AJ invoice PDF (api/invoice-pdf) and open it in a new tab. Throws on failure. */
export async function openInvoicePdf(invoiceId) {
  const token = await authToken()
  const billedByPhase = await previouslyBilledByPhase(invoiceId)   // for the Previously Billed column
  const summary = await buildInvoiceSummary(invoiceId)             // Core-style summary footer
  const res = await fetch('/api/invoice-pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ invoiceId, billedByPhase, summary }),
  })
  if (!res.ok) { let e = {}; try { e = await res.json() } catch (_) {} ; throw new Error(e.error || ('PDF failed: ' + res.status)) }
  const url = URL.createObjectURL(await res.blob())
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 60000)
}

// ── Project statement ───────────────────────────────────────────────────────────────
// A statement = the project's currently-owed invoices on one list with a single Total Now Due
// (what we re-issue when a client has open balances). Built from the cached Core invoices, scoped
// to the project's root Core UID. "Owed" = posted (status 1) invoices with an open balance, plus
// this cycle's drafts being issued (the new invoice + any interest charge). Billed-to-date spans the
// account (posted + issuing drafts, incl. fully paid); paid-to-date = billed − owed.
export async function buildStatementData(rootCoreUid) {
  if (!rootCoreUid) return null
  const invoices = await getCoreInvoicesCached()
  const belongs = inv => (inv.invoiceDetails || []).some(d => d.rootProjectId === rootCoreUid)
  const lines = []
  let billedToDate = 0, totalNowDue = 0
  const dbg = []
  for (const inv of (invoices || [])) {
    if (inv.isVoid || !belongs(inv)) continue
    const interest = _isInterestInv(inv)
    const posted = inv.status === 1, draft = !!inv.isDraft
    const amt = Number(inv.invoiceAmount) || 0, bal = Number(inv.balance) || 0
    // Account = posted invoices + this cycle's issuing drafts + any OPEN interest invoice. Interest is
    // often still unposted/non-draft, so gate it on an open balance (not post state) — same as the
    // invoice summary; otherwise the interest charge silently drops off the statement.
    const include = posted || draft || (interest && bal > 0.005)
    dbg.push({ num: inv.invoiceNumber, interest, status: inv.status, draft, bal: Math.round(bal * 100) / 100, amt, include })
    if (!include) continue
    billedToDate += amt
    const owed = (posted && bal > 0.005) || (draft && amt > 0.005) || (interest && bal > 0.005)
    if (!owed) continue
    const lineBal = draft ? amt : bal
    totalNowDue += lineBal
    lines.push({ id: inv.id, number: inv.invoiceNumber, date: inv.date, amount: amt, balance: lineBal, interest, draft })
  }
  lines.sort((a, b) => new Date(a.date) - new Date(b.date))
  const paidToDate = Math.max(0, billedToDate - totalNowDue)
  try { console.log('[billing] statement ' + rootCoreUid + ' — total=' + Math.round(totalNowDue * 100) / 100 + ' billed=' + Math.round(billedToDate * 100) / 100 + ' lines=' + lines.length, dbg) } catch (e) {}
  return { lines, count: lines.length, totalNowDue, billedToDate, paidToDate }
}

/** Render the AJ project statement PDF (api/statement-pdf) and open it in a new tab. The statement
 *  page is followed by each unpaid invoice (newest → oldest) in the same file — no manual combine.
 *  Throws on failure. */
export async function openStatementPdf(rootCoreUid) {
  const statement = await buildStatementData(rootCoreUid)
  if (!statement || !statement.count) throw new Error('No open invoices to include on a statement.')
  // Each owed invoice, newest → oldest, with its own derived summary + previously-billed map (same
  // inputs as a single invoice preview) so the appended invoices render exactly like their originals.
  const ordered = statement.lines.slice().sort((a, b) => new Date(b.date) - new Date(a.date))
  const invoices = []
  for (const ln of ordered) {
    if (!ln.id) continue
    invoices.push({ id: ln.id, summary: await buildInvoiceSummary(ln.id), billedByPhase: await previouslyBilledByPhase(ln.id) })
  }
  const token = await authToken()
  const res = await fetch('/api/statement-pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ rootProjectId: rootCoreUid, statement, invoices }),
  })
  if (!res.ok) { let e = {}; try { e = await res.json() } catch (_) {} ; throw new Error(e.error || ('Statement failed: ' + res.status)) }
  const url = URL.createObjectURL(await res.blob())
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 60000)
}

/** Fetch Core's OWN invoice PDF (the exact file behind the pay page's "View Invoice") and open it.
 *  This is what the client sees, so it stays consistent end-to-end. Throws on failure. */
export async function openCoreInvoicePdf(invoiceId) {
  const token = await authToken()
  const res = await fetch('/api/core-invoice-pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ invoiceId }),
  })
  if (!res.ok) { let e = {}; try { e = await res.json() } catch (_) {} ; throw new Error(e.error || ('Core PDF failed: ' + res.status)) }
  const url = URL.createObjectURL(await res.blob())
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 60000)
}

/** Open the raw invoice JSON (every field) in a new tab — for verifying what data we can pull. */
export async function openInvoiceData(invoiceId) {
  const token = await authToken()
  const res = await fetch('/api/invoice-pdf', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ invoiceId, format: 'json' }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error((data && data.error) || ('Data fetch failed: ' + res.status))
  try { console.log('[billing] invoice ' + invoiceId + ' fields:', Object.keys(data).join(',')) } catch (e) {}
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }))
  window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 60000)
  return data
}

// ── Project statement "pay all" link (Stripe, generated per project PER BILLING RUN) ─────────────
// Scoped to (projectId, runId) — a link captured in one month's run must not silently read back as
// "already done" in a later month's run for the same project. See docs/BILLING_BQE.md's "Statement
// state is scoped per billing run" entry and docs/STRIPE_PAYMENTS.md for why this is Stripe now.
/** The generated statement pay-all link for this project, scoped to one billing run. */
export async function getStatementPayLink(projectId, runId) {
  const token = await authToken()
  const qs = 'projectId=' + encodeURIComponent(projectId) + (runId ? '&runId=' + encodeURIComponent(runId) : '')
  const res = await fetch('/api/billing-statement-link?' + qs, {
    headers: { ...(token ? { Authorization: 'Bearer ' + token } : {}) },
  })
  // {} — NOT { statementPayUrl: null }. A null here means "asked, and there is no link"; a failed
  // request has not established that. Returning null made the Billing Run offer "Generate pay link"
  // on projects that already had one, because the caller could not tell the two apart.
  if (!res.ok) return {}
  return res.json()
}
/**
 * Generate (or reuse, if already captured for this run) the Stripe "pay whole balance" link —
 * bundles every currently-open Core invoice for the project into one Stripe invoice. Server-side
 * only; no URL is supplied by the caller (see api/billing-statement-link.js).
 */
export function generateStatementPayLink(projectId, runId) {
  return authedPost('/api/billing-statement-link', { projectId, runId })
}
/**
 * Stripe's view of each project's payments, for the Billing Run list — processing, stalled at bank
 * verification, failed, or collected. Reads our own recorded rows (written by the Stripe webhook),
 * never Stripe itself, which is what makes it affordable for every row at once rather than only the
 * project you have open.
 *
 * NOT reconciliation: a project reading paid still needs that payment applied in BQE Core by hand.
 */
export function getPaymentActivity(projectIds) {
  return authedPost('/api/billing-payment-activity', { projectIds })
}

/** Clear the captured statement link for this project + billing run (leaves other runs' links alone). */
export async function clearStatementPayLink(projectId, runId) {
  const token = await authToken()
  const res = await fetch('/api/billing-statement-link', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify({ projectId, runId }),
  })
  return res.json().catch(() => ({}))
}

// ── Client invoice/statement email ──────────────────────────────────────────────────
/**
 * Preview (send=false, default) or send (send=true) the client-facing invoice/statement email with
 * the Core PDF attached. Preview returns { to, subject, html, text, attachment, enabled } without
 * sending. With send=true it sends only if sending is switched on server-side; otherwise it returns
 * { skipped:true, reason:'sending_disabled', ...preview }. Action-aware via `mode` ('invoice'|'statement').
 * `customMessage` is an optional one-off note (never stored) rendered as its own paragraph in the body.
 */
export function sendInvoiceEmail({ projectId, invoiceId, mode, invoiceNumber, payUrl, to, cc, bcc, salutation, customMessage, send, test, fresh, isReminder }) {
  const payload = { projectId, invoiceId, mode, invoiceNumber, payUrl, to, cc, bcc, send: !!send, test: !!test, fresh: !!fresh, isReminder: !!isReminder }
  if (typeof salutation === 'string') payload.salutation = salutation
  if (typeof customMessage === 'string') payload.customMessage = customMessage
  return authedPost('/api/billing-send-invoice', payload)
}

// ── Read an invoice's posted status from BQE Core ───────────────────────────────────
/** Live check: { posted, status, isDraft, invoiceNumber, balance } for a Core invoice. */
export function getCoreInvoiceStatus(invoiceId) {
  return authedPost('/api/bqe-invoice-status', { invoiceId })
}

// ── Post an invoice in BQE Core (un-posted → posted) ────────────────────────────────
/** Post a finalized invoice in Core. Returns { ok, posted, status, invoiceNumber } / { error }. */
export function postInvoiceInCore(invoiceId) {
  return authedPost('/api/bqe-post-invoice', { invoiceId })
}

// ── Core invoice-numbering re-calibration follow-ups ────────────────────────────────
/** Open follow-ups (projects whose Core auto-numbering needs recalibrating). */
export async function listRenumberFollowups() {
  const token = await authToken()
  const res = await fetch('/api/billing-renumber-followups', { headers: token ? { Authorization: 'Bearer ' + token } : {} })
  if (!res.ok) return { followups: [] }
  return res.json()
}
/** Flag a project as needing Core renumber recalibration (idempotent per project). */
export function flagRenumberFollowup({ projectId, label, coreUid, coreUrl, reason }) {
  return authedPost('/api/billing-renumber-followups', { action: 'flag', projectId, label, coreUid, coreUrl, reason })
}
/** Mark a project's Core renumbering recalibrated (resolved). */
export function resolveRenumberFollowup(projectId) {
  return authedPost('/api/billing-renumber-followups', { action: 'resolve', projectId })
}

// ── Slack reminder (server reads bot token) ─────────────────────────────────────────
/** DM a member to get time current. blocks optional. Returns { ok } / { ok:false, error }. */
export function remindMember({ slackUserId, text, blocks }) {
  return authedPost('/api/billing-remind', { slackUserId, text, blocks })
}
